import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { endpoints, asset } from "@/lib/api";
import { CTASection } from "@/components/site/Bits";
import { Reveal, LineReveal, ParallaxImage } from "@/lib/motion";

function Fact({ label, value }) {
  return (
    <div className="border-t border-aria-travertine pt-5">
      <div className="text-[0.65rem] uppercase tracking-[0.22em] text-aria-muted">{label}</div>
      <div className="mt-2 font-display text-2xl text-aria-ink">{value}</div>
    </div>
  );
}

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [cs, setCs] = useState(null);

  useEffect(() => {
    endpoints.caseStudy(slug).then((d) => {
      setCs(d);
      document.title = `${d.title} — ARIA Case Study`;
    }).catch(() => {});
  }, [slug]);

  if (!cs) return <div className="h-screen" />;

  return (
    <div data-testid="case-study-detail">
      <section className="relative h-[85svh] w-full overflow-hidden aria-grain">
        <img src={asset(cs.hero_image)} alt={cs.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-aria-ink/30" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-aria-ink/90 via-aria-ink/30 to-transparent" />
        <div className="relative z-10 flex h-full items-end">
          <div className="aria-container pb-20">
            <div className="mb-6 text-[0.72rem] uppercase tracking-[0.28em] text-aria-ivory/80">
              Case Study · {cs.location}
            </div>
            <h1 className="max-w-4xl font-display text-aria-ivory text-4xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight">
              <LineReveal lines={[cs.title]} />
            </h1>
          </div>
        </div>
      </section>

      {/* Facts + overview */}
      <section className="aria-container py-24 md:py-36">
        <div className="grid grid-cols-1 gap-16 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="grid grid-cols-1 gap-8">
              <Fact label="Client" value={cs.client_type} />
              <Fact label="Venue" value={cs.venue} />
              <Fact label="Guests" value={cs.guest_count} />
              <Fact label="Year" value={cs.year} />
            </div>
          </div>
          <div className="md:col-span-8">
            <Reveal>
              <p className="font-display text-2xl md:text-3xl lg:text-4xl leading-[1.2] text-aria-ink text-balance">{cs.overview}</p>
            </Reveal>
            {cs.objectives?.length > 0 && (
              <div className="mt-14">
                <div className="eyebrow mb-6">The Brief</div>
                <ul className="divide-y divide-aria-travertine border-t border-aria-travertine">
                  {cs.objectives.map((o, i) => (
                    <li key={i} className="flex gap-6 py-5">
                      <span className="font-accent italic text-xl text-aria-brass">{String(i + 1).padStart(2, "0")}</span>
                      <p className="font-light text-lg text-aria-cocoa/80">{o}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Cocktail menu */}
      {cs.cocktail_menu?.length > 0 && (
        <section className="bg-aria-ink text-aria-ivory py-24 md:py-36" data-testid="case-menu">
          <div className="aria-container">
            <div className="eyebrow mb-12">The Menu</div>
            <div className="grid grid-cols-1 gap-x-16 gap-y-10 md:grid-cols-3">
              {cs.cocktail_menu.map((c, i) => (
                <Reveal key={i} delay={(i % 3) * 0.1}>
                  <div className="border-t border-aria-ivory/15 pt-6">
                    <h3 className="font-display text-3xl text-aria-ivory">{c.name}</h3>
                    <p className="mt-3 font-light text-aria-ivory/60">{c.notes}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {cs.gallery?.length > 0 && (
        <section className="aria-container py-24 md:py-36" data-testid="case-gallery">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {cs.gallery.map((g, i) => (
              <ParallaxImage key={i} src={g} alt={`${cs.title} ${i + 1}`} className={`w-full ${i % 3 === 0 ? "md:col-span-2 aspect-[16/9]" : "aspect-[4/3]"}`} strength={40} />
            ))}
          </div>
        </section>
      )}

      {/* Testimonial + lessons */}
      {cs.testimonial && (
        <section className="bg-aria-sand py-24 md:py-36">
          <div className="aria-container">
            <Reveal>
              <blockquote className="mx-auto max-w-4xl text-center">
                <p className="font-display text-3xl md:text-5xl leading-[1.15] text-aria-ink text-balance">“{cs.testimonial.quote}”</p>
                <footer className="mt-8 text-[0.72rem] uppercase tracking-[0.24em] text-aria-muted">
                  {cs.testimonial.author} — {cs.testimonial.role}
                </footer>
              </blockquote>
            </Reveal>
          </div>
        </section>
      )}

      {cs.lessons && (
        <section className="aria-container py-24 md:py-32">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
            <div className="md:col-span-4"><div className="eyebrow">In Reflection</div></div>
            <div className="md:col-span-8">
              <p className="font-light text-xl md:text-2xl leading-relaxed text-aria-cocoa/85">{cs.lessons}</p>
            </div>
          </div>
        </section>
      )}

      <CTASection />
    </div>
  );
}
